import {
  Text,
  Banner,
  Button,
  Checkbox,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyWhite,
  IconPlus,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  ShadowXs,
  TextInputField,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import { isEmail, required } from '@sequencehq/utils/validation'
import { useForm } from '@sequencehq/utils'
import { useMemo, useState } from 'react'
import { useTeamSettings } from '../useTeamSettings'
import { SYSTEM_ROLES } from 'lib/rbac/systemRoles'
import {
  ROLE_OPTIONS,
  ROLE_SELECTION_HELP_TEXT,
  ROLE_DOCS_URL
} from './utils/roleOptions'
import { useRoleManagementCapabilities } from './utils/useRoleManagementCapabilities'

export const InviteMemberModalV2 = () => {
  const { inviteMemberWithRoles, isInvitePending } = useTeamSettings()
  const { canInvite, canAssignRoles, defaultInviteRoles, getSubmissionRoles } =
    useRoleManagementCapabilities()

  const initialFormValue = useMemo(
    () => ({
      email: '',
      roles: [...defaultInviteRoles]
    }),
    [defaultInviteRoles]
  )

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { fields, queries, mutators } = useForm({
    value: initialFormValue,
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'email',
        validation: [isEmail, required]
      },
      {
        property: 'roles',
        validation: canAssignRoles ? [required] : []
      }
    ]
  })

  if (!canInvite) {
    return null
  }

  const isAdminSelected = queries.formData.roles.includes(SYSTEM_ROLES.admin)
  const roleOptions = ROLE_OPTIONS
  const rolesToAssign = getSubmissionRoles(queries.formData.roles)

  return (
    <SimpleModal
      trigger={
        <Button
          variant="primary"
          data-testid="team.invite.trigger"
          onClick={() => {
            setShowValidationErrors(false)
            mutators.resetFormData()
          }}
          leadingIcon={<IconPlus />}
        >
          New user
        </Button>
      }
      minHeight="0px"
      data-testid="team.invite.modal"
      submitDisabled={!queries.isValid}
      title="Invite user"
      submitButtonText="Send invite"
      submittingButtonText="Sending..."
      onSubmit={async () => {
        if (!queries.isValid) return false

        await inviteMemberWithRoles({
          email: queries.formData.email,
          roles: rolesToAssign
        })

        return true
      }}
    >
      {isAdminSelected && (
        <Box marginBottom="16px">
          <Banner variant="warning">
            Admins can manage all settings and users. Assign carefully
          </Banner>
        </Box>
      )}
      <Flex
        id="invite-member-form"
        data-testid="team.invite.form"
        flexDirection="column"
        gap="16px"
      >
        <Flex flexDirection="column" gap="8px">
          <Box {...Primary13SemiBold} color={GreyGrey70}>
            Email
          </Box>
          <TextInputField
            autoFocus
            id="memberEmail"
            data-testid="team.invite.email"
            placeholder="amy@example.com"
            disabled={isInvitePending}
            value={fields.email.value}
            validationErrors={fields.email.validationErrors}
            onChange={e => {
              fields.email.onChange(e.target.value)
            }}
          />
        </Flex>

        {canAssignRoles && (
          <Flex
            data-testid="team.invite.rolesSection"
            flexDirection="column"
            gap="12px"
          >
            <Flex flexDirection="column" gap="4px">
              <Box {...Primary13SemiBold} color={GreyGrey70}>
                Role
              </Box>
              <Text {...Primary13Regular} color={GreyGrey70}>
                {ROLE_SELECTION_HELP_TEXT}{' '}
                <Link.Unstyled
                  style={{ textDecoration: 'none', color: IndigoIndigo60 }}
                  to={ROLE_DOCS_URL}
                  target="_blank"
                >
                  learn more.
                </Link.Unstyled>
              </Text>
            </Flex>

            <Flex
              data-testid="team.invite.roleList"
              borderRadius="8px"
              border={`1px solid ${GreyGrey40}`}
              boxShadow={ShadowXs}
              backgroundColor={GreyWhite}
              flexDirection="column"
            >
              {roleOptions.map((role, index) => (
                <Flex
                  padding="16px"
                  key={role.value}
                  borderBottom={
                    index === roleOptions.length - 1
                      ? 'none'
                      : `1px solid ${GreyGrey40}`
                  }
                >
                  <Checkbox
                    data-testid={`team.invite.role.${role.value}`}
                    isChecked={fields.roles.value.includes(role.value)}
                    onChange={e => {
                      const currentRoles = fields.roles.value
                      const newRoles = e.target.checked
                        ? [...currentRoles, role.value]
                        : currentRoles.filter(r => r !== role.value)
                      fields.roles.onChange(newRoles)
                    }}
                    label={
                      <Flex flexDirection="column" gap="4px">
                        <Text {...Primary13SemiBold}>{role.label}</Text>
                        <Text {...Primary13Regular} color={GreyGrey60}>
                          {role.description}
                        </Text>
                      </Flex>
                    }
                  />
                </Flex>
              ))}
            </Flex>

            {fields.roles.validationErrors &&
              fields.roles.validationErrors.length > 0 && (
                <Text
                  {...Primary13Regular}
                  data-testid="team.invite.rolesError"
                  color="red.500"
                >
                  {String(fields.roles.validationErrors[0])}
                </Text>
              )}
          </Flex>
        )}
      </Flex>
    </SimpleModal>
  )
}
